import {
  interaction,
  SizableText,
  styled,
  XStack,
  YStack,
  YStackProps,
} from '@mythical/ui'
import React from 'react'
import ContentThumbnail, { ContentThumbnailProps } from '../ContentThumbnail'
import { SmartLink } from 'app/components/primitive/SmartLink'

const CardWrapper = styled(YStack, {
  fs: 1,
  gap: '$2',
  fd: 'column',
  jc: 'flex-start',
  ai: 'flex-start',
  width: '100%',
})

const CardContent = styled(YStack, {
  gap: '$1.5',
  width: '100%',
  fs: 1,
  fg: 1,
  fd: 'column',
  jc: 'flex-start',
  ai: 'flex-start',
  $gtSm: {
    gap: '$2',
  },
})

const CardHeading = styled(SizableText, {
  color: '$fgPrimary',
  ff: '$body',
  size: '$1',
  fow: '500',
  $gtSm: {
    fow: '500',
    size: '$2',
  },
})

const CardText = styled(SizableText, {
  color: '$fgSecondary',
  ff: '$body',
  size: '$0.75',
  $gtSm: {
    size: '$1',
  },
})

export interface ContentEpisodeListItemProps {
  id: string
  image: string
  title: string
  episodeNumber: number
  episodeName?: string
  episodePart?: number
  seriesOverlayUrl?: string
  href: string
  isActive?: boolean
  wrapperProps?: YStackProps
  thumbnailProps?: ContentThumbnailProps['wrapperProps']
  onNavigate?: (e: React.BaseSyntheticEvent) => void
}

const ContentEpisodeListItem = ({
  image,
  title,
  href,
  isActive,
  episodeNumber = 1,
  episodePart,
  wrapperProps,
  onNavigate,
  episodeName,
  seriesOverlayUrl,
  thumbnailProps,
}: ContentEpisodeListItemProps) => {
  // When onNavigate is provided, we want to handle navigation in-place
  // without pushing a new screen onto the stack
  const handleThumbnailPress = onNavigate ? onNavigate : undefined
  const handleTextPress = onNavigate
    ? (e: React.BaseSyntheticEvent) => {
        e?.preventDefault?.()
        onNavigate(e)
      }
    : undefined

  return (
    <CardWrapper {...wrapperProps}>
      <YStack width="100%" {...thumbnailProps}>
        <ContentThumbnail
          isActive={isActive}
          href={onNavigate ? undefined : href}
          image={image}
          onPress={handleThumbnailPress}
          seriesOverlayUrl={seriesOverlayUrl}
          sizes="(min-width: 661px) 350px, 170px"
        />
      </YStack>

      <SmartLink
        Component={CardContent}
        href={onNavigate ? undefined : href}
        onPress={handleTextPress}
        replace
        cursor="pointer"
        {...interaction.opacity}
      >
        {(episodeNumber || episodePart) && (
          <XStack gap="$1.5">
            {episodeNumber > 0 && (
              <CardText textTransform="uppercase">
                {episodeName && episodeName?.length > 1
                  ? episodeName
                  : `EP ${episodeNumber}`}
              </CardText>
            )}
            {!!episodePart && episodePart > 0 && (
              <CardText tt="uppercase">PT {episodePart}</CardText>
            )}
          </XStack>
        )}
        <CardHeading numberOfLines={1}>{title}</CardHeading>
      </SmartLink>
    </CardWrapper>
  )
}

export default ContentEpisodeListItem
